import { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { PollProgressResponseBody } from '@atlassiansox/asc-cofs';
import { Environment, ProductKey, Url } from '@atlassiansox/bxpkit-core';
import { catalog } from '@atlassiansox/metal-client';
import { metalClient } from '../../../metrics';
import { generateConfirmationUrl } from '../../../service/sign-up-form/form-submit';
import { DevopsState } from '../hook';
import { messages } from '../messages';
import { FormDataN2N } from '../types';
import { getSiteDomain } from './';
import {
  LoggedInFormDataType,
  updateSharedFormDataGoToProductUrl,
  updateSharedFormDataSubmitErrorMessage
} from '../../../redux';

type PollForSuccessType = {
  readonly progressUri: string;
  readonly confirmationPageUrl: string;
  readonly pollProgress: () => Promise<PollProgressResponseBody>;
  readonly devopsState: DevopsState;
  readonly data?: FormDataN2N;
  readonly dispatch: any;
  readonly loggedInFormData?: LoggedInFormDataType;
  readonly onInitialRequestSuccess?: (cloudId: string) => void;
  readonly createAnalyticsEvent: CreateUIAnalyticsEvent;
};

export function pollForSuccess({
  progressUri,
  confirmationPageUrl,
  pollProgress,
  devopsState,
  data,
  onInitialRequestSuccess,
  createAnalyticsEvent,
  dispatch,
  loggedInFormData
}: PollForSuccessType) {
  const wacEnvUrl = Environment.getWacIntegrationUrl();
  const { productKeys, userInfo, locale } = devopsState;

  const isJswIncluded = loggedInFormData?.productsExisting?.includes(
    ProductKey.JIRA_SOFTWARE
  );

  return new Promise((resolve, reject) => {
    let secondsPassed = 0;

    const poll = setInterval(async () => {
      secondsPassed++;
      if (secondsPassed === 5) {
        dispatch(
          updateSharedFormDataSubmitErrorMessage(messages.provisioningTakesTime)
        );
      }
      if (secondsPassed === 15) {
        createAnalyticsEvent({
          eventName: 'bxp.formCloudSignup.waitedSignupPageSubmit>15s',
          eventComponent: 'button',
          event: 'clicked'
        }).fire();
      }
      if (secondsPassed === 60) {
        createAnalyticsEvent({
          eventName: 'bxp.formCloudSignup.waitedSignupPageSubmit>60s',
          eventComponent: 'button',
          event: 'clicked'
        }).fire();
      }
      if (secondsPassed === 180) {
        createAnalyticsEvent({
          eventName: 'bxp.formCloudSignup.waitedSignupPageSubmit>180s',
          eventComponent: 'button',
          event: 'clicked'
        }).fire();
        metalClient.metric.submit({
          name: catalog.userInteraction.TASK_FAILURE,
          page: 'sign-up-layout-devops',
          task: 'devops-form-submit'
        });
        if (userInfo?.isLoggedIn) {
          dispatch(
            updateSharedFormDataSubmitErrorMessage(messages.errorTimeoutN2E)
          );
        } else {
          dispatch(
            updateSharedFormDataSubmitErrorMessage(messages.errorTimeoutN2N)
          );
        }
        clearInterval(poll);
        resolve('180_SEC_TIME_OUT');
        // setPollingLoaderShown(false);
      }

      const body = await pollProgress();
      if (!body) {
        clearInterval(poll);
        createAnalyticsEvent({
          eventName: 'bxp.formCloudSignup.progressPollMissing',
          eventComponent: 'button',
          event: 'clicked',
          data: {
            label: body
          }
        }).fire();
      }

      if (body) {
        const cloudId = body.cloudId;
        const productParam = productKeys.map((p) => p.product).join(',');
        const cloudName =
          data?.sitenameNew || loggedInFormData?.cloudName || '';
        const dataRegion = locale.locale;

        const statusActivate = body.bestStatuses.ACTIVATE_PRODUCT;
        const statusSignup =
          body.bestStatuses.HAMS_CREATE_CLOUD !== 'SKIPPED'
            ? body.bestStatuses.HAMS_CREATE_CLOUD
            : body.bestStatuses.BAG_CREATE_CLOUD;

        if (cloudId && onInitialRequestSuccess) {
          onInitialRequestSuccess(cloudId);
        }

        // runs on activation requests
        if (statusActivate && body.completed) {
          clearInterval(poll);
          metalClient.metric.submit({
            name: catalog.userInteraction.TASK_DURATION,
            page: 'sign-up-layout-devops',
            task: 'devops-form-submit',
            value: secondsPassed * 1000
          });

          if (body.successful) {
            let confirmUrl = generateConfirmationUrl({
              wacEnvUrl,
              confirmationPageUrl,
              cloudId,
              progressUri,
              cloudName,
              products: productParam,
              isOpsgenieIncluded: true,
              dataRegion,
              isDevops: true
            });
            if (isJswIncluded) {
              const queryParameters = Url.getUrlParams() || {};
              const signupSource = queryParameters.hasOwnProperty(
                'signupSource'
              )
                ? `&signupSource${queryParameters.signupSource}`
                : '';
              confirmUrl = `https://${cloudName}${getSiteDomain()}/jira/devops/connected-project?isFoundingAdmin=true${signupSource}`;
            }
            dispatch(updateSharedFormDataGoToProductUrl(confirmUrl));
            createAnalyticsEvent({
              eventName: 'bxp.formCloudSignup.submitSuccess',
              eventComponent: 'button',
              event: 'clicked',
              data: {
                type: 'N2E'
              }
            }).fire();
            createAnalyticsEvent({
              eventName: 'bxp.formCloudSignup.confirmedEvaluation',
              eventComponent: 'button',
              event: 'clicked'
            }).fire();
            metalClient.metric.submit({
              name: catalog.userInteraction.TASK_SUCCESS,
              page: 'sign-up-layout-devops',
              task: 'devops-form-submit'
            });
            dispatch(updateSharedFormDataSubmitErrorMessage(undefined));
            resolve(statusActivate);
            // }
          } else {
            metalClient.metric.submit({
              name: catalog.userInteraction.TASK_FAILURE,
              page: 'sign-up-layout-devops',
              task: 'devops-form-submit'
            });
            createAnalyticsEvent({
              eventName: 'bxp.formCloudSignup.progressPollError',
              eventComponent: 'button',
              event: 'clicked',
              data: {
                label: body
              }
            }).fire();
          }
          // TODO: else if successful === false, throw error/show in UI
        }

        // runs on signup requests
        switch (statusSignup) {
          // @ts-ignore
          case 'COMPLETE_SUCCESS':
            // @ts-ignore
            // case 'COMPLETE_MANUAL_SUCCESS':
            clearInterval(poll);
            metalClient.metric.submit({
              name: catalog.userInteraction.TASK_DURATION,
              page: 'sign-up-layout-devops',
              task: 'devops-form-submit',
              value: secondsPassed * 1000
            });
            createAnalyticsEvent({
              eventName: 'apollo.signup.success',
              eventComponent: 'button',
              event: 'clicked'
            }).fire();

            // TODO if (!cloudId) throw Error;

            if (!cloudId) {
              createAnalyticsEvent({
                eventName: 'bxp.formCloudSignup.progressPollMissing',
                eventComponent: 'button',
                event: 'clicked',
                data: {
                  label: 'cloudId'
                }
              }).fire();
            } else {
              createAnalyticsEvent({
                eventName: 'bxp.formCloudSignup.submitSuccess',
                eventComponent: 'button',
                event: 'clicked',
                data: {
                  type: userInfo?.isLoggedIn ? 'N2E' : 'N2N'
                }
              }).fire();
              metalClient.metric.submit({
                name: catalog.userInteraction.TASK_SUCCESS,
                page: 'sign-up-layout-devops',
                task: 'devops-form-submit'
              });
            }
            if (userInfo?.isLoggedIn) {
              const confirmUrl = generateConfirmationUrl({
                wacEnvUrl,
                confirmationPageUrl,
                cloudId,
                progressUri,
                cloudName,
                products: productParam,
                isOpsgenieIncluded: true,
                dataRegion,
                isDevops: true
              });
              dispatch(updateSharedFormDataGoToProductUrl(confirmUrl));
              dispatch(updateSharedFormDataSubmitErrorMessage(undefined));
              resolve(statusSignup);
            } else {
              sessionStorage.setItem(
                'r.e.d',
                btoa(
                  JSON.stringify({
                    cloudId,
                    cloudName,
                    email: data?.email,
                    pagePath: 'account/setup',
                    requestId: body.requestId
                  })
                )
              );
              dispatch(updateSharedFormDataSubmitErrorMessage(undefined));
              resolve(statusSignup);
            }
            return;
          case 'COMPLETE_ERROR':
            clearInterval(poll);
            reject('error'); // testing rejection, sh
            // newRelic.action('apollo.error.progressUri', {
            //   ...body.bestStatuses
            // });
            // segment('bxp.formCloudSignup.progressPollError', {
            //   label: statusSignup
            // });
            // TODO: show error on frontend

            return;
        }
      }
    }, 1000);
  });
}
